import React, { FC, useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography, useTheme } from '@mui/material';

import LegendItem from '@/components/Charts/LegendItem';

import { format } from './utils';

interface TalentsTabProps {
  chartSize: number;
  counters: {
    total?: number;
    inProgress?: number;
    completed?: number;
    registered?: number;
  };
}

const TalentsTab: FC<TalentsTabProps> = ({ chartSize, counters }) => {
  const { palette, spacing } = useTheme();
  const { t } = useTranslation(['processes', 'common']);

  const countersList = useMemo(
    () => [counters?.completed || 0, counters?.inProgress || 0, counters?.registered || 0],
    [counters]
  );

  const chartData = useMemo(
    () => ({
      datasets: [
        {
          data: countersList,
          backgroundColor: [palette.primary[500], palette.primary[300], palette.primary[250]],
          borderWidth: 0,
          cutout: '70%'
        }
      ]
    }),
    [palette, countersList]
  );

  return (
    <Box sx={{ my: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', p: spacing(3, 0.5) }}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <Doughnut
            data={chartData}
            options={{ cutout: '70%', responsive: false }}
            width={chartSize}
            height={chartSize}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography variant="h6" color="primary">
              {counters.total ?? 0}
            </Typography>
          </Box>
        </Box>
        <Stack
          sx={{
            marginInlineStart: 5,
            marginInlineEnd: 0,
            textAlign: 'start',
            gap: 1
          }}
        >
          <LegendItem
            title={t('layouts.processes.details.info.talentCounters.done')}
            color="primary.500"
            value={format(t, 'common:number.percent', counters.completed! / (counters.total || 0))}
          />
          <LegendItem
            title={t('layouts.processes.details.info.talentCounters.inTests')}
            color="primary.300"
            value={format(t, 'common:number.percent', counters.inProgress! / (counters.total || 0))}
          />
          <LegendItem
            title={t('layouts.processes.details.info.talentCounters.registered')}
            color="primary.250"
            value={format(t, 'common:number.percent', counters.registered! / (counters.total || 0))}
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default TalentsTab;
